import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Utensils, MapPin, Star, ExternalLink, Filter } from 'lucide-react';
import { toast } from 'sonner';

function SlimUitEtenPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState({ dietary: '', provider: '' });

  const dummyRestaurants = [
    { id: 1, name: 'Istanbul Grill', cuisine: 'Turks', dietary_tags: ['halal'], price_per_person: 12.50, rating: 4.5, provider: 'thuisbezorgd', deeplink: 'https://www.thuisbezorgd.nl' },
    { id: 2, name: 'Veggie Heaven', cuisine: 'Vegan', dietary_tags: ['vegan', 'vegetarian'], price_per_person: 15.00, rating: 4.8, provider: 'ubereats', deeplink: 'https://www.ubereats.com' },
    { id: 3, name: 'Pasta Perfetto', cuisine: 'Italiaans', dietary_tags: ['vegetarian'], price_per_person: 14.00, rating: 4.3, provider: 'thuisbezorgd', deeplink: 'https://www.thuisbezorgd.nl' },
    { id: 4, name: 'Riad Marrakech', cuisine: 'Marokkaans', dietary_tags: ['halal'], price_per_person: 13.50, rating: 4.6, provider: 'ubereats', deeplink: 'https://www.ubereats.com' },
    { id: 5, name: 'Sushi Master', cuisine: 'Japans', dietary_tags: [], price_per_person: 18.00, rating: 4.7, provider: 'thuisbezorgd', deeplink: 'https://www.thuisbezorgd.nl' },
    { id: 6, name: 'Green Bowl', cuisine: 'Vegan', dietary_tags: ['vegan', 'kosher'], price_per_person: 11.00, rating: 4.4, provider: 'lieferando', deeplink: 'https://www.lieferando.de' }
  ];

  const filteredRestaurants = dummyRestaurants.filter(r => {
    if (filters.dietary && !r.dietary_tags.includes(filters.dietary)) return false;
    if (filters.provider && r.provider !== filters.provider) return false;
    return true;
  });

  return (
    <div className="py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="flex items-center gap-3 mb-8">
          <div className="bg-secondary rounded-full p-3">
            <Utensils className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-4xl font-heading font-bold text-[#2C3E2D]" data-testid="slim-uit-eten-title">
              {t('slim_uit_eten.title')}
            </h1>
            <p className="text-gray-600">{t('slim_uit_eten.subtitle')}</p>
          </div>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="w-5 h-5" />
              {t('slim_uit_eten.filters')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">{t('slim_uit_eten.diet')}</label>
                <Select value={filters.dietary} onValueChange={(v) => setFilters({...filters, dietary: v})}>
                  <SelectTrigger data-testid="filter-dietary">
                    <SelectValue placeholder={t('slim_uit_eten.all_diets')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('slim_uit_eten.all_diets')}</SelectItem>
                    <SelectItem value="halal">{t('slim_uit_eten.halal')}</SelectItem>
                    <SelectItem value="kosher">{t('slim_uit_eten.kosher')}</SelectItem>
                    <SelectItem value="vegetarian">{t('slim_uit_eten.vegetarian')}</SelectItem>
                    <SelectItem value="vegan">{t('slim_uit_eten.vegan')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">{t('slim_uit_eten.provider')}</label>
                <Select value={filters.provider} onValueChange={(v) => setFilters({...filters, provider: v})}>
                  <SelectTrigger data-testid="filter-provider">
                    <SelectValue placeholder={t('slim_uit_eten.all_providers')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('slim_uit_eten.all_providers')}</SelectItem>
                    <SelectItem value="thuisbezorgd">Thuisbezorgd</SelectItem>
                    <SelectItem value="ubereats">Uber Eats</SelectItem>
                    <SelectItem value="lieferando">Lieferando</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => setFilters({ dietary: '', provider: '' })}
                  data-testid="reset-filters-btn"
                >
                  {t('slim_uit_eten.reset')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {filteredRestaurants.length > 0 ? (
          <div className="grid md:grid-cols-2 gap-6">
            {filteredRestaurants.map((restaurant) => (
              <Card key={restaurant.id} className="hover:shadow-lg transition-shadow" data-testid={`restaurant-${restaurant.id}`}>
                <CardContent className="pt-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-heading font-bold mb-1">{restaurant.name}</h3>
                      <p className="text-sm text-gray-600">{restaurant.cuisine}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      <span className="font-semibold">{restaurant.rating}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mb-4">
                    <MapPin className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-600 capitalize">{restaurant.provider}</span>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {restaurant.dietary_tags.map((tag, idx) => (
                      <span key={idx} className="px-2 py-1 bg-success/10 text-success rounded text-xs capitalize">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t">
                    <div>
                      <p className="text-sm text-gray-600">{t('slim_uit_eten.estimated_pp')}</p>
                      <p className="text-xl font-bold text-primary">&euro;{restaurant.price_per_person.toFixed(2)}</p>
                    </div>
                    <Button
                      onClick={() => {
                        window.open(restaurant.deeplink, '_blank');
                        toast.success(t('slim_uit_eten.order'));
                      }}
                      className="bg-secondary hover:bg-secondary-hover text-white"
                      data-testid={`order-${restaurant.id}`}
                    >
                      {t('slim_uit_eten.order')}
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Utensils className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">{t('slim_uit_eten.no_restaurants')}</h3>
            <p className="text-gray-500">{t('slim_uit_eten.adjust_filters')}</p>
          </div>
        )}

        <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-gray-700">
            <strong>{t('slim_uit_eten.disclaimer')}</strong>
          </p>
        </div>
      </div>
    </div>
  );
}

export default SlimUitEtenPage;
